import React, { useState, useEffect, useRef, useCallback, useMemo, lazy, Suspense } from 'react';
import { createPortal } from 'react-dom';
import { trackInteraction, EventTypes, initClickTracking, initScrollDepthTracking } from './tracking-utils';
import Intro from './Intro';
import { FEATURED, HERO, SCORES, DEFAULT_SCORE } from './curation';
import PALETTE from './palette';
import { normalizePlace } from './places';
import ColourWheel, { COLOUR_FAMILIES } from './ColourWheel';
import './gallery.scss';

// The map, its geometry and coordinates only load when someone opens it.
const Atlas = lazy(() => import('./Atlas'));

const API_URL = "https://www.api.kenankorn.com";

// ---------------------------------------------------------------------------
// Data helpers
// ---------------------------------------------------------------------------

// Some S3 filenames contain literal spaces ("_DSC3485-Header export-2.jpg");
// browsers tolerate them in src but fetch/preload APIs do not.
const safeUrl = (url) => (url ? url.replace(/ /g, '%20') : '');

const thumbSrc = (image) => safeUrl(image.urls?.thumbnail || image.file_src);
const mediumSrc = (image) => safeUrl(image.urls?.medium || image.file_src);
const largeSrc = (image) => safeUrl(image.urls?.large || image.urls?.medium || image.file_src);
const originalSrc = (image) => safeUrl(image.file_src || image.urls?.original);

// Recency key — most recently uploaded first; the admin-curated `order` breaks
// ties within batches that share an upload timestamp.
const sortKey = (img) => {
  const uploaded = img.created_at?.seconds || 0;
  const order = typeof img.order === 'number' ? img.order : 0;
  return uploaded * 1e6 + order;
};

const scoreOf = (img) => SCORES[img.id] ?? DEFAULT_SCORE;

// Curated order (see curation.js): the FEATURED welcome sequence first, then
// everything else best-first with recency breaking ties. Duplicate uploads of
// the same original file are dropped (the higher-scored copy is kept).
const FEATURED_RANK = new Map(FEATURED.map((id, i) => [id, i]));
const DIVERSITY_WINDOW = 8;  // how far ahead to look for a different place
const RECENT_CITIES = 3;     // a city may not repeat within this many frames

const curatedOrder = (imagesData) => {
  const byFile = new Map();
  for (const img of imagesData) {
    const file = img.metadata?.originalName || img.id;
    const kept = byFile.get(file);
    if (!kept || scoreOf(img) > scoreOf(kept)) byFile.set(file, img);
  }
  const unique = [...byFile.values()];

  const featured = unique
    .filter((img) => FEATURED_RANK.has(img.id))
    .sort((a, b) => FEATURED_RANK.get(a.id) - FEATURED_RANK.get(b.id));
  const pool = unique
    .filter((img) => !FEATURED_RANK.has(img.id))
    .sort((a, b) => scoreOf(b) - scoreOf(a) || sortKey(b) - sortKey(a));

  // Keep runs of the same place from clumping: if the next frame shares a
  // city with any of the last few placed, pull forward the nearest one that
  // doesn't (only from a short look-ahead, so ranking stays essentially intact).
  const out = featured.slice();
  while (pool.length) {
    const recent = new Set(out.slice(-RECENT_CITIES).map((img) => img.city).filter(Boolean));
    const alt = pool.slice(0, DIVERSITY_WINDOW).findIndex((img) => !recent.has(img.city));
    out.push(pool.splice(Math.max(alt, 0), 1)[0]);
  }
  return out;
};

// Colour order (see palette.js): one continuous walk around the colour wheel,
// red → orange → gold → green → teal → blue → violet → magenta, closing on
// the monochrome frames fading light → dark into the footer. Pure hue order
// flickers between shadowy and bright frames of the same hue, so the walk is
// smoothed: from each frame it steps to whichever of the next few (by hue)
// is closest in overall tone, so the hue keeps advancing but tones blend.
const HUE_START = 345;
const COLOUR_LOOKAHEAD = 7;

// Colour-wheel filter: a frame belongs to a family when at least this % of it
// is clearly that colour (palette.js `mix`). Results lead with the frames
// where the colour is strongest; monochrome is the frames with no real hue.
const FAMILY_MIN_COVER = 5;

const inFamily = (img, f) => (PALETTE[img.id]?.mix?.[f] ?? 0) >= FAMILY_MIN_COVER;
const isMono = (img) => PALETTE[img.id] != null && PALETTE[img.id].hue == null;

const colourFilter = (images, filter) => {
  if (filter === 'mono') {
    return images.filter(isMono).sort((a, b) => PALETTE[b.id].light - PALETTE[a.id].light);
  }
  return images
    .filter((img) => inFamily(img, filter))
    .sort((a, b) => PALETTE[b.id].mix[filter] - PALETTE[a.id].mix[filter]);
};

const hexRgb = (h) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16) / 255);

const colourOrder = (images) => {
  const hueKey = (img) => (PALETTE[img.id].hue - HUE_START + 360) % 360;
  const remaining = images
    .filter((img) => PALETTE[img.id]?.hue != null)
    .sort((a, b) => hueKey(a) - hueKey(b));
  const mono = images.filter((img) => PALETTE[img.id]?.hue == null);

  const tone = (img) => {
    const p = PALETTE[img.id];
    return { hue: hueKey(img), light: p.light, rgb: hexRgb(p.bg) };
  };
  const distance = (a, b) =>
    Math.abs(a.hue - b.hue) / 40 +
    Math.abs(a.light - b.light) * 2.2 +
    Math.hypot(a.rgb[0] - b.rgb[0], a.rgb[1] - b.rgb[1], a.rgb[2] - b.rgb[2]);

  const out = [];
  while (remaining.length) {
    let k = 0;
    if (out.length) {
      const prev = tone(out[out.length - 1]);
      let best = Infinity;
      remaining.slice(0, COLOUR_LOOKAHEAD).forEach((img, i) => {
        const d = distance(prev, tone(img));
        if (d < best) { best = d; k = i; }
      });
    }
    out.push(remaining.splice(k, 1)[0]);
  }
  // Frames without palette data yet (uploaded since the last extraction)
  // join the monochrome coda rather than breaking the gradient.
  mono.sort((a, b) => (PALETTE[b.id]?.light ?? 0) - (PALETTE[a.id]?.light ?? 0));
  return out.concat(mono);
};

// ---------------------------------------------------------------------------
// Editorial layout — 24 slots on a 12-column grid, composed in bands so no
// frame (especially a tall vertical) ever sits alone in an empty row.
// [colStart, colSpan], aspect-ratio, vertical offset (vw), full-bleed flag
// ---------------------------------------------------------------------------
const PATTERN = [
  // big opener + companion
  { col: [1, 7], ar: '3 / 2', off: 0 },
  { col: [9, 3], ar: '2 / 3', off: 5 },
  // pair
  { col: [2, 4], ar: '3 / 4', off: 0 },
  { col: [7, 5], ar: '16 / 10', off: 6 },
  // full-bleed pano
  { col: [1, 12], ar: '21 / 9', off: 8, bleed: true },
  // triple with a centred vertical
  { col: [1, 4], ar: '4 / 5', off: 0 },
  { col: [6, 3], ar: '1 / 2.2', off: 4 },
  { col: [10, 3], ar: '2 / 3', off: 9 },
  // wide + edge peek
  { col: [3, 7], ar: '21 / 10', off: 0 },
  { col: [11, 2], ar: '2 / 3', off: 6 },
  // triple
  { col: [1, 3], ar: '2 / 3', off: 0 },
  { col: [5, 4], ar: '3 / 4', off: 5 },
  { col: [10, 3], ar: '4 / 5', off: 10 },
  // full-bleed wide
  { col: [1, 12], ar: '3 / 1', off: 8, bleed: true },
  // horizontal + vertical pair
  { col: [2, 5], ar: '16 / 9', off: 0 },
  { col: [8, 4], ar: '1 / 2.4', off: 4 },
  // pair
  { col: [1, 6], ar: '3 / 2', off: 0 },
  { col: [8, 5], ar: '4 / 5', off: 7 },
  // vertical + wide pair
  { col: [2, 3], ar: '1 / 2.2', off: 0 },
  { col: [6, 6], ar: '3 / 2', off: 3 },
  // triple
  { col: [1, 4], ar: '3 / 4', off: 0 },
  { col: [6, 3], ar: '2 / 3', off: 7 },
  { col: [10, 3], ar: '4 / 5', off: 3 },
  // big closer
  { col: [4, 8], ar: '16 / 9', off: 7 },
];

const STRIP_PICKS = [0, 3, 5, 9, 14, 7, 11];
const STRIP_WIDTH_CLASS = ['is-wide', 'is-narrow', 'is-broad'];
const SECTION_SIZE = 24;          // images per grid section (one full pattern)
const INTERLUDE_EVERY = 2;        // horizontal strip after every 2nd section
const INITIAL_SECTIONS = 1;
const MOBILE_WIDTHS = ['100%', '74%', '58%', '88%', '66%'];

const slotFor = (i) => PATTERN[i % PATTERN.length];

const pad2 = (n) => String(n + 1).padStart(2, '0');

// ---------------------------------------------------------------------------
// No force-cropping: photos render at their own aspect ratio inside the
// pattern's column geometry (every image doc carries pixel dimensions). The
// only enforced crops are the two full-bleed panoramas per section — each
// section hands those to its widest images, and when even the widest is too
// square the slot falls back to a large centred frame at natural aspect.
// ---------------------------------------------------------------------------
const imageRatio = (img) =>
  img.width && img.height ? img.width / img.height : null;

const BLEED_POSITIONS = PATTERN.map((slot, i) => (slot.bleed ? i : -1)).filter((i) => i >= 0);

const arrangeForSlots = (images) => {
  const out = [];
  for (let start = 0; start < images.length; start += PATTERN.length) {
    const chunk = images.slice(start, start + PATTERN.length);
    if (chunk.length === PATTERN.length) {
      for (const pos of BLEED_POSITIONS) {
        let best = -1;
        let bestR = -1;
        chunk.forEach((img, k) => {
          if (BLEED_POSITIONS.includes(k) && k < pos) return; // keep earlier pano picks
          const r = imageRatio(img) || 0;
          if (r > bestR) { bestR = r; best = k; }
        });
        if (best !== -1 && best !== pos) {
          const tmp = chunk[pos];
          chunk[pos] = chunk[best];
          chunk[best] = tmp;
        }
      }
    }
    out.push(...chunk);
  }
  return out;
};

// The frame renders at very different sizes across slots — serve the variant
// that stays sharp at that size (image quality is the product here).
const slotSrc = (image, slot, isMobile) => {
  if (slot.bleed) return originalSrc(image);
  if (isMobile) return mediumSrc(image);
  if (slot.col[1] >= 6) return largeSrc(image);
  return mediumSrc(image);
};

// ---------------------------------------------------------------------------
// Image with blur-up: a soft thumbnail paints almost immediately underneath
// while the full-quality variant streams in and fades over it.
// ---------------------------------------------------------------------------
const Frame = ({ src, placeholder, alt, eager = false, onRatio }) => {
  // Track *which* src finished loading, not merely that one did: a bare
  // boolean stays true when the src changes underneath a reused Frame (the
  // hero is the one that isn't keyed by image id), which hard-cuts the new
  // photograph in at full opacity and skips the blur-up entirely.
  const [loadedSrc, setLoadedSrc] = useState(null);
  const loaded = loadedSrc === src;
  return (
    <>
      {placeholder && !loaded && (
        <img
          className="kk-img kk-img--placeholder"
          src={placeholder}
          alt=""
          aria-hidden="true"
          loading={eager ? 'eager' : 'lazy'}
          decoding="async"
          draggable={false}
        />
      )}
      <img
        className={`kk-img ${loaded ? 'is-loaded' : ''}`}
        src={src}
        alt={alt}
        loading={eager ? 'eager' : 'lazy'}
        decoding="async"
        fetchpriority={eager ? 'high' : undefined}
        draggable={false}
        onLoad={(e) => {
          setLoadedSrc(src);
          if (onRatio && e.target.naturalWidth && e.target.naturalHeight) {
            onRatio(e.target.naturalWidth / e.target.naturalHeight);
          }
        }}
      />
    </>
  );
};

// ---------------------------------------------------------------------------
// Lightbox modal
// ---------------------------------------------------------------------------
const Lightbox = ({ images, index, onClose, onGo, isMobile }) => {
  const image = images[index];
  const [full, setFull] = useState(false);
  const stripRef = useRef(null);
  const touchRef = useRef(null);
  const closeBtnRef = useRef(null);

  const fullSrc = useCallback(
    (img) => (isMobile ? largeSrc(img) : originalSrc(img)),
    [isMobile]
  );

  // The grid's medium cut is usually already cached — show it soft while the
  // full-quality version streams in, then snap to sharp.
  useEffect(() => {
    setFull(false);
    if (!image) return undefined;
    const img = new Image();
    img.onload = () => setFull(true);
    img.onerror = () => setFull(true);
    img.src = fullSrc(image);
    return () => { img.onload = null; img.onerror = null; };
  }, [image, fullSrc]);

  // Preload neighbours so Prev/Next feel instant.
  useEffect(() => {
    [index + 1, index - 1].forEach((n) => {
      const neighbour = images[(n + images.length) % images.length];
      if (neighbour) {
        const img = new Image();
        img.src = fullSrc(neighbour);
      }
    });
  }, [index, images, fullSrc]);

  // Keyboard
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') onGo(index + 1);
      else if (e.key === 'ArrowLeft') onGo(index - 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, onClose, onGo]);

  // Scroll lock + focus
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeBtnRef.current?.focus();
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Keep the active thumbnail centred
  useEffect(() => {
    const strip = stripRef.current;
    const active = strip?.querySelector(`[data-thumb="${index}"]`);
    if (strip && active) {
      strip.scrollTo({
        left: active.offsetLeft - strip.clientWidth / 2 + active.offsetWidth / 2,
        behavior: 'smooth',
      });
    }
  }, [index]);

  if (!image) return null;

  const onTouchStart = (e) => {
    touchRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  };
  const onTouchEnd = (e) => {
    const start = touchRef.current;
    if (!start) return;
    const dx = e.changedTouches[0].clientX - start.x;
    const dy = e.changedTouches[0].clientY - start.y;
    touchRef.current = null;
    if (Math.abs(dx) > 56 && Math.abs(dx) > Math.abs(dy) * 1.4) {
      onGo(index + (dx < 0 ? 1 : -1));
    } else if (dy > 80 && Math.abs(dy) > Math.abs(dx) * 1.4) {
      onClose();
    }
  };

  return createPortal(
    <div
      className="kk-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={`${image.city}, ${image.country}`}
    >
      <div className="kk-lightbox__top">
        <div className="kk-lightbox__title" key={image.id}>
          <h3><span>{image.city}</span></h3>
          <p><span>{image.country} · {pad2(index)} / {images.length}</span></p>
        </div>
        <button
          ref={closeBtnRef}
          className="kk-lightbox__close"
          onClick={onClose}
          aria-label="Close"
        >
          ✕
        </button>
      </div>

      <div
        className="kk-lightbox__stage"
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {images.length > 1 && (
          <button
            className="kk-lightbox__arrow is-prev"
            onClick={() => onGo(index - 1)}
            aria-label="Previous photograph"
          >
            <span aria-hidden="true">←</span>
          </button>
        )}

        <img
          key={image.id}
          className={`kk-lightbox__img ${full ? 'is-full' : 'is-soft'}`}
          src={full ? fullSrc(image) : mediumSrc(image)}
          alt={`${image.city}, ${image.country}`}
          draggable={false}
        />

        {images.length > 1 && (
          <button
            className="kk-lightbox__arrow is-next"
            onClick={() => onGo(index + 1)}
            aria-label="Next photograph"
          >
            <span aria-hidden="true">→</span>
          </button>
        )}
      </div>

      <div className="kk-lightbox__bottom">
        <div className="kk-lightbox__thumbs" ref={stripRef}>
          {images.map((thumb, i) => (
            <button
              key={thumb.id}
              data-thumb={i}
              className={`kk-lightbox__thumb ${i === index ? 'is-active' : ''}`}
              onClick={() => onGo(i)}
              aria-label={`${thumb.city}, frame ${pad2(i)}`}
            >
              <img src={thumbSrc(thumb)} alt="" loading="lazy" decoding="async" draggable={false} />
            </button>
          ))}
        </div>
      </div>
    </div>,
    document.body
  );
};

// ---------------------------------------------------------------------------
// Gallery page
// ---------------------------------------------------------------------------
const Gallery = () => {
  const [images, setImages] = useState([]);
  const [boot, setBoot] = useState('loading'); // loading | ready | error
  const [openIndex, setOpenIndex] = useState(-1);
  // 'curated' — the hand-picked best-first sequence; 'colour' — the same
  // archive walked around the colour wheel (see colourOrder).
  const [mode, setMode] = useState('curated');
  // Colour mode only: null (all), a COLOUR_FAMILIES index, or 'mono'.
  const [hueFilter, setHueFilter] = useState(null);
  // The Atlas lives at #atlas so it can be linked to and closed with Back.
  const [atlasOpen, setAtlasOpen] = useState(
    () => typeof window !== 'undefined' && window.location.hash === '#atlas'
  );
  const atlasPushedRef = useRef(false);
  // Lightbox opened from the Atlas: its own image set (one place's frames).
  const [atlasView, setAtlasView] = useState(null);
  const [visibleSections, setVisibleSections] = useState(INITIAL_SECTIONS);
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth < 700
  );

  // Opening sequence (see Intro.jsx) — plays on every full page load.
  // prefers-reduced-motion doesn't skip it; it swaps in a gentle
  // crossfade-only variant (reduced motion means calmer, not nothing).
  const [intro, setIntro] = useState(true);
  const introMode = useRef(intro).current;
  const [introDone, setIntroDone] = useState(!introMode);
  const reducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  useEffect(() => {
    if (!intro) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [intro]);

  const handleIntroReveal = useCallback(() => setIntroDone(true), []);
  const handleIntroDone = useCallback(() => setIntro(false), []);

  const rootRef = useRef(null);
  const railNumRef = useRef(null);
  const sentinelRef = useRef(null);

  // Runtime-measured aspect ratios for images whose docs predate dimension
  // capture (keyed by image id, set once on first load).
  const [measuredRatios, setMeasuredRatios] = useState({});
  const noteRatio = useCallback((id, ratio) => {
    setMeasuredRatios((prev) => (prev[id] ? prev : { ...prev, [id]: ratio }));
  }, []);

  // ---- data ----------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;

    const apply = (imagesData) => {
      if (cancelled) return;
      setImages(curatedOrder(imagesData.map(normalizePlace)));
      setBoot('ready');
    };

    // Repeat visits render instantly from the cached payload while a fresh
    // copy revalidates in the background.
    let hadCache = false;
    try {
      const cached = JSON.parse(localStorage.getItem('kk_gallery_v1'));
      if (cached && Array.isArray(cached.images) && cached.images.length) {
        hadCache = true;
        apply(cached.images);
      }
    } catch (err) { /* stale or corrupt cache — ignore */ }

    (async () => {
      try {
        const imagesRes = await fetch(`${API_URL}/images`);
        if (!imagesRes.ok) throw new Error('fetch failed');
        const imagesData = await imagesRes.json();
        apply(imagesData);
        try {
          localStorage.setItem('kk_gallery_v1', JSON.stringify({ images: imagesData }));
        } catch (err) { /* storage full — fine */ }
      } catch (err) {
        console.error('Failed to load gallery data:', err);
        if (!cancelled && !hadCache) setBoot('error');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ---- tracking ------------------------------------------------------------
  useEffect(() => {
    trackInteraction(EventTypes.PAGE_VIEW, {
      referrer: document.referrer || 'direct',
      isInitialLoad: true,
      pageType: 'gallery',
    });
    // Capture-phase clicks + max-scroll-depth milestones; the depth tracker
    // flushes the session max as PAGE_EXIT on visibilitychange/pagehide.
    const offClicks = initClickTracking();
    const offScrollDepth = initScrollDepthTracking();
    return () => { offClicks(); offScrollDepth(); };
  }, []);

  // ---- responsive ----------------------------------------------------------
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 700);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const modeRef = useRef(mode);
  modeRef.current = mode;

  const sequence = useMemo(() => {
    if (mode !== 'colour') return arrangeForSlots(images);
    return hueFilter == null ? colourOrder(images) : colourFilter(images, hueFilter);
  }, [images, mode, hueFilter]);

  const familyCounts = useMemo(
    () => COLOUR_FAMILIES.map((_, f) => images.filter((img) => inFamily(img, f)).length),
    [images]
  );
  const monoCount = useMemo(() => images.filter(isMono).length, [images]);

  const changeHueFilter = useCallback((next) => {
    trackInteraction(EventTypes.PAGE_CHANGE, {
      view: 'sequence',
      mode: 'colour',
      colour: next == null ? 'all' : next === 'mono' ? 'mono' : COLOUR_FAMILIES[next].key,
    });
    setHueFilter(next);
    setVisibleSections(INITIAL_SECTIONS);
    document.getElementById('sequence')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const switchMode = useCallback((next) => {
    if (next === mode) return;
    trackInteraction(EventTypes.PAGE_CHANGE, { view: 'sequence', mode: next });
    setMode(next);
    setHueFilter(null);
    document.getElementById('sequence')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [mode]);

  // ---- atlas ---------------------------------------------------------------
  useEffect(() => {
    const onHash = () => {
      const open = window.location.hash === '#atlas';
      setAtlasOpen(open);
      if (!open) { atlasPushedRef.current = false; setAtlasView(null); }
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const openAtlas = useCallback((e) => {
    e.preventDefault();
    trackInteraction(EventTypes.PAGE_CHANGE, { view: 'atlas' });
    atlasPushedRef.current = true;
    window.history.pushState(null, '', '#atlas');
    setAtlasOpen(true);
  }, []);

  const closeAtlas = useCallback(() => {
    setAtlasView(null);
    setAtlasOpen(false);
    if (window.location.hash !== '#atlas') return;
    // Opened from the page: step back so Back doesn't reopen it. Arrived on a
    // #atlas link: just drop the hash.
    if (atlasPushedRef.current) {
      atlasPushedRef.current = false;
      window.history.back();
    } else {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  }, []);

  const openFromAtlas = useCallback((set, index) => {
    const image = set[index];
    trackInteraction(EventTypes.VIEW_IMAGE, {
      imageId: image.id,
      imageName: image.name,
      imageLocation: { city: image.city || 'unknown', country: image.country || 'unknown' },
      source: 'atlas',
      position: index,
    });
    setAtlasView({ images: set, index });
  }, []);

  // The opening frame rotates daily through the curated HERO frames — but it is chosen
  // once per page load and then held.
  //
  // A warm visit renders the cached payload first and the network copy a beat
  // later. Deriving the pick from `day % length` against each of those arrays
  // lands on a different photograph the moment the archive has grown or shrunk
  // since the cache was written, swapping the hero out from under the title
  // mid-load — and breaking the intro's promise that the hero beneath holds
  // the same photograph it just swept in. Holding by id keeps the daily
  // rotation (a fresh load re-picks) without the mid-load switch.
  const heroIdRef = useRef(null);
  const hero = useMemo(() => {
    if (!images.length) return undefined;
    const held = heroIdRef.current
      ? images.find((img) => img.id === heroIdRef.current)
      : undefined;
    // `held` is undefined only on the first pick, or if that photo has since
    // been removed from the archive — both cases legitimately re-pick.
    // Rotate through the curated HERO frames; fall back to the whole archive
    // if none of them are in the archive any more.
    const heroPool = images.filter((img) => HERO.includes(img.id));
    const pool = heroPool.length ? heroPool : images;
    const pick = held || pool[Math.floor(Date.now() / 86400000) % pool.length];
    heroIdRef.current = pick?.id ?? null;
    return pick;
  }, [images]);

  // Sections + interludes, limited by progressive mount
  const blocks = useMemo(() => {
    const out = [];
    const total = Math.ceil(sequence.length / SECTION_SIZE);
    const shown = Math.min(visibleSections, total);
    for (let s = 0; s < shown; s++) {
      const start = s * SECTION_SIZE;
      const items = sequence.slice(start, start + SECTION_SIZE);
      out.push({ type: 'grid', start, items, key: `g-${start}` });
      if ((s + 1) % INTERLUDE_EVERY === 0 && sequence.length > SECTION_SIZE) {
        // Sample 7 frames spread across the sections since the last interlude.
        const rangeStart = (s - INTERLUDE_EVERY + 1) * SECTION_SIZE;
        const picks = STRIP_PICKS
          .map((p) => rangeStart + p * INTERLUDE_EVERY * 3)
          .map((p) => Math.min(p, sequence.length - 1));
        const unique = [...new Set(picks)].filter((p) => sequence[p]);
        if (unique.length > 2) {
          out.push({ type: 'strip', picks: unique, key: `s-${start}` });
        }
      }
    }
    out.hasMore = shown < total;
    return out;
  }, [sequence, visibleSections]);

  // ---- reveal on scroll ----------------------------------------------------
  useEffect(() => {
    if (boot !== 'ready') return undefined;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const root = rootRef.current;
    if (!root) return undefined;
    if (reduced) {
      root.querySelectorAll('[data-reveal]').forEach((n) => n.classList.add('in'));
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in');
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -6% 0px', threshold: 0.04 }
    );
    root.querySelectorAll('[data-reveal]:not(.in)').forEach((n) => io.observe(n));
    return () => io.disconnect();
  }, [boot, blocks]);

  // ---- progressive mount ---------------------------------------------------
  useEffect(() => {
    if (boot !== 'ready' || !blocks.hasMore) return undefined;
    const sentinel = sentinelRef.current;
    if (!sentinel) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setVisibleSections((v) => v + 2);
        }
      },
      { rootMargin: '1600px 0px' }
    );
    io.observe(sentinel);
    return () => io.disconnect();
  }, [boot, blocks.hasMore]);

  // ---- scroll-driven effects: interlude drift + frame rail -----------------
  useEffect(() => {
    if (boot !== 'ready') return undefined;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let raf = 0;
    const update = () => {
      raf = 0;
      const vh = window.innerHeight;
      if (!reduced) {
        document.querySelectorAll('[data-hstrip]').forEach((strip) => {
          const rect = strip.getBoundingClientRect();
          if (rect.bottom > -200 && rect.top < vh + 200) {
            const prog = 1 - (rect.top + rect.height / 2) / (vh + rect.height / 2);
            const range = Math.max(0, strip.scrollWidth - window.innerWidth);
            const x = -Math.min(1, Math.max(0, prog)) * range;
            strip.style.transform = `translate3d(${x}px,0,0)`;
          }
        });
      }
      const railNum = railNumRef.current;
      if (railNum) {
        let best = null;
        let bestDist = Infinity;
        document.querySelectorAll('figure[data-idx]').forEach((fig) => {
          const rect = fig.getBoundingClientRect();
          if (rect.bottom < 0 || rect.top > vh) return;
          const dist = Math.abs(rect.top + rect.height / 2 - vh / 2);
          if (dist < bestDist) { bestDist = dist; best = fig; }
        });
        if (best) {
          const n = pad2(Number(best.dataset.idx));
          if (railNum.textContent !== n) railNum.textContent = n;
        }
        // Colour mode: the page ground takes on a breath of the frame in view.
        const root = rootRef.current;
        if (root) {
          const accent = modeRef.current === 'colour' && best ? best.dataset.accent : '';
          if (root.style.getPropertyValue('--kk-ambient') !== (accent || '')) {
            if (accent) root.style.setProperty('--kk-ambient', accent);
            else root.style.removeProperty('--kk-ambient');
          }
        }
      }
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    update();
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [boot, blocks]);

  // ---- modal ---------------------------------------------------------------
  const openModal = useCallback((i) => {
    const image = sequence[i];
    if (!image) return;
    trackInteraction(EventTypes.VIEW_IMAGE, {
      imageId: image.id,
      imageName: image.name,
      imageLocation: { city: image.city || 'unknown', country: image.country || 'unknown' },
      collectionName: image.collection,
      position: i,
    });
    setOpenIndex(i);
  }, [sequence]);

  const goModal = useCallback((i) => {
    setOpenIndex((i + sequence.length) % sequence.length);
  }, [sequence.length]);

  // ---- figure renderer -----------------------------------------------------
  const renderFigure = (image, seqIndex, patternIndex) => {
    // Doc dimensions first; images uploaded before the backend stored them
    // get measured at load time instead of falling back to a fixed crop.
    const natural = imageRatio(image) ?? measuredRatios[image.id] ?? null;
    let slot = slotFor(patternIndex);
    // A panorama slot with no genuinely wide image becomes a large centred
    // frame at the photo's own aspect instead of a brutal crop.
    if (slot.bleed && (natural == null || natural < 1.3)) {
      slot = { col: [3, 8], ar: slot.ar, off: slot.off };
    }
    // Outside the panoramas, frames take the photo's own aspect — no cropping.
    const aspect = slot.bleed ? slot.ar : natural ? natural.toFixed(4) : slot.ar;
    const style = isMobile
      ? {
          width: slot.bleed ? '100%' : MOBILE_WIDTHS[patternIndex % MOBILE_WIDTHS.length],
          alignSelf: patternIndex % 2 ? 'flex-end' : 'flex-start',
          marginTop: seqIndex === 0 ? 0 : `${(8 + (slot.off % 6) * 1.5).toFixed(1)}vh`,
        }
      : {
          gridColumn: `${slot.col[0]} / span ${slot.col[1]}`,
          marginTop: `${slot.off}vw`,
        };
    const src = slotSrc(image, slot, isMobile);
    const heavy = slot.bleed || slot.col[1] >= 6 || isMobile;
    return (
      <figure
        key={image.id}
        data-reveal=""
        data-idx={seqIndex}
        data-image-id={image.id}
        data-image-name={image.name}
        data-accent={PALETTE[image.id]?.accent}
        className={`kk-figure ${slot.bleed && !isMobile ? 'is-bleed' : ''}`}
        style={style}
        onClick={() => openModal(seqIndex)}
      >
        <div
          className="kk-figure__frame"
          // The frame's own average colour shows while the photo streams in.
          style={{ aspectRatio: aspect, backgroundColor: PALETTE[image.id]?.bg }}
        >
          <Frame
            src={src}
            placeholder={heavy ? thumbSrc(image) : undefined}
            alt={`${image.city}, ${image.country}`}
            onRatio={natural == null ? (r) => noteRatio(image.id, r) : undefined}
          />
          <span className="kk-figure__view" aria-hidden="true">View</span>
        </div>
        <figcaption className="kk-figure__caption">
          <span className="kk-num">{pad2(seqIndex)}</span>
          <span className="kk-city">{image.city}</span>
          <span className="kk-country">{image.country}</span>
          <span className="kk-figure__cta">View ↗</span>
        </figcaption>
      </figure>
    );
  };

  // ---- render --------------------------------------------------------------
  return (
    <div
      className={`kk ${introMode ? 'has-intro' : ''} ${introDone ? 'intro-done' : ''} ${mode === 'colour' ? 'is-colour' : ''}`}
      ref={rootRef}
      id="top"
    >
      {intro && (
        <Intro
          image={hero}
          count={sequence.length}
          reduced={reducedMotion}
          onReveal={handleIntroReveal}
          onDone={handleIntroDone}
        />
      )}

      <header className="kk-header">
        <a href="#top" className="kk-header__mark" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
          Kenan Korn
        </a>
        <nav className="kk-header__nav">
          <a href="#atlas" onClick={openAtlas}>Atlas</a>
          <a href="https://www.instagram.com/kenan_ko_/" target="_blank" rel="noopener noreferrer">Instagram</a>
        </nav>
      </header>

      <div className="kk-rail" aria-hidden="true">
        <span className="kk-rail__now" ref={railNumRef}>01</span>
        <span className="kk-rail__line" />
        <span className="kk-rail__total">{sequence.length || '—'}</span>
      </div>

      <section className="kk-hero">
        <div className="kk-hero__bg">
          {hero && (
            <Frame
              src={originalSrc(hero)}
              placeholder={mediumSrc(hero)}
              alt=""
              eager
            />
          )}
        </div>
        <div className="kk-hero__scrim" aria-hidden="true" />
        <div className="kk-hero__content">
          <h1 className="kk-hero__name">
            <span className="kk-mask"><span className="kk-riser kk-riser--1">Kenan</span></span>
            <span className="kk-mask"><span className="kk-riser kk-riser--2 is-italic">Korn</span></span>
          </h1>
        </div>
        <div className="kk-hero__scroll" aria-hidden="true">
          <span /><em>Scroll</em><span />
        </div>
      </section>

      <main className="kk-sequence" id="sequence">
        {boot === 'ready' && (
          <div className="kk-order" role="group" aria-label="Sequence order">
            <span className="kk-order__label">Sequence</span>
            <button
              className={`kk-order__opt ${mode === 'curated' ? 'is-on' : ''}`}
              aria-pressed={mode === 'curated'}
              onClick={() => switchMode('curated')}
            >
              Curated
            </button>
            <button
              className={`kk-order__opt kk-order__opt--colour ${mode === 'colour' ? 'is-on' : ''}`}
              aria-pressed={mode === 'colour'}
              onClick={() => switchMode('colour')}
            >
              Colour <span className="kk-order__spectrum" aria-hidden="true" />
            </button>
          </div>
        )}

        {boot === 'ready' && mode === 'colour' && (
          <ColourWheel
            counts={familyCounts}
            monoCount={monoCount}
            value={hueFilter}
            onChange={changeHueFilter}
          />
        )}

        {boot === 'loading' && (
          <div className="kk-grid" aria-hidden="true">
            {PATTERN.slice(0, 8).map((slot, i) => (
              <div
                key={i}
                className={`kk-figure kk-skel ${slot.bleed ? 'is-bleed' : ''}`}
                style={isMobile
                  ? { width: slot.bleed ? '100%' : MOBILE_WIDTHS[i % MOBILE_WIDTHS.length], alignSelf: i % 2 ? 'flex-end' : 'flex-start', marginTop: i === 0 ? 0 : '9vh' }
                  : { gridColumn: `${slot.col[0]} / span ${slot.col[1]}`, marginTop: `${slot.off}vw` }}
              >
                <div className="kk-figure__frame" style={{ aspectRatio: slot.ar }} />
              </div>
            ))}
          </div>
        )}

        {boot === 'error' && (
          <p className="kk-error">The gallery could not be reached — please try again in a moment.</p>
        )}

        {blocks.map((block) =>
          block.type === 'grid' ? (
            <div className="kk-grid" key={block.key}>
              {block.items.map((image, i) =>
                renderFigure(image, block.start + i, block.start + i)
              )}
            </div>
          ) : (
            <section className="kk-interlude" data-reveal="" key={block.key}>
              <div className="kk-interlude__head">
                <span>Interlude — moving through</span>
                <span className="kk-interlude__arrow">↔</span>
              </div>
              <div className="kk-interlude__strip" data-hstrip="">
                {block.picks.map((seqIndex, k) => {
                  const image = sequence[seqIndex];
                  return (
                    <button
                      key={`${image.id}-${k}`}
                      className={`kk-interlude__item ${STRIP_WIDTH_CLASS[k % 3]}`}
                      onClick={() => openModal(seqIndex)}
                    >
                      <div className="kk-interlude__frame">
                        <Frame src={mediumSrc(image)} alt={`${image.city}, ${image.country}`} />
                      </div>
                      <span className="kk-num">{pad2(seqIndex)}</span>
                    </button>
                  );
                })}
              </div>
            </section>
          )
        )}

        {blocks.hasMore && <div className="kk-sentinel" ref={sentinelRef} aria-hidden="true" />}
      </main>

      <footer className="kk-footer">
        <div className="kk-footer__main">
          <div className="kk-footer__cta">
            <span className="kk-footer__label">Prints and commissions on request</span>
            <a
              className="kk-footer__big"
              href="https://www.instagram.com/kenan_ko_/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Instagram <span className="kk-footer__arrow">→</span>
            </a>
          </div>
          <div className="kk-footer__links">
            <a href="#atlas" onClick={openAtlas}>Atlas</a>
            <a href="#top" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>↑ Top</a>
          </div>
        </div>
        <div className="kk-footer__base">
          <span className="kk-footer__mark">Kenan Korn</span>
          <span>© {new Date().getFullYear()} — all frames by Kenan Korn</span>
        </div>
      </footer>

      {atlasOpen && !intro && boot === 'ready' && (
        <Suspense fallback={null}>
          <Atlas
            images={images}
            onClose={closeAtlas}
            onOpenImage={openFromAtlas}
            paused={!!atlasView}
          />
        </Suspense>
      )}

      {atlasView && (
        <Lightbox
          images={atlasView.images}
          index={atlasView.index}
          onClose={() => setAtlasView(null)}
          onGo={(i) => setAtlasView((v) => v && {
            ...v,
            index: (i + v.images.length) % v.images.length,
          })}
          isMobile={isMobile}
        />
      )}

      {openIndex >= 0 && (
        <Lightbox
          images={sequence}
          index={openIndex}
          onClose={() => setOpenIndex(-1)}
          onGo={goModal}
          isMobile={isMobile}
        />
      )}
    </div>
  );
};

export default Gallery;
